/* Copy that Bassem edits, over copy the design decided.
 *
 * Bassem's ruling, 2026-09-26: he manages ALL site text himself from Admin.
 * This is the machinery under that — and it is deliberately thin, because the
 * rule it has to keep is thin:
 *
 *   THE i18n FILES ARE THE DEFAULTS. `site_texts` only ever holds OVERRIDES.
 *   A missing row means "use the designed wording"; deleting a row restores
 *   it. There is no state in which the site has no words.
 *
 * NO COMPONENT CHANGES. The screens keep reading `s.hero.title` and
 * `t.nav.signIn` as they always did. What changed is that `s` and `t` are
 * built per render by folding the overrides into the dictionary, so every one
 * of those strings became editable without a single call site moving. A
 * refactor to `t("hero.title")` everywhere would have been the same feature
 * with a thousand chances to typo a key.
 *
 * PLAIN TEXT, ALWAYS. Values are rendered as text nodes like every other
 * string, so a stored `<b>` shows as `<b>`. The worker refuses markup at the
 * door as well; two guards, because the one that matters is whichever is
 * still there after the next change.
 */

export type TextMap = Record<string, string>;
export type LocaleTexts = Record<string, TextMap>;

/* Which dictionary a key came from. The rebuilt screens and the ones still on
 * the previous shell have separate dictionaries, and two keys called `nav.buy`
 * would otherwise be one row. */
export type Namespace = "v3" | "app";

/* ── flattening ──────────────────────────────────────────────────────────── */

/** Every string leaf of a dictionary, as `a.b.c` (and `a.b.0` for arrays). */
export function flatten(obj: unknown, prefix = "", out: TextMap = {}): TextMap {
  if (typeof obj === "string") {
    out[prefix] = obj;
    return out;
  }
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => flatten(v, prefix ? `${prefix}.${i}` : String(i), out));
    return out;
  }
  if (obj && typeof obj === "object") {
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      flatten(v, prefix ? `${prefix}.${k}` : k, out);
    }
  }
  return out;
}

/* Keys that are NOT visible copy, and must not appear in the editor.
 *
 * Every one of these is a value that happens to be a string and is not a
 * sentence anybody reads: a locale code used as an attribute, the console's
 * own chrome (the ruling says every public screen), a direction. Putting them
 * in front of an editor would invite someone to translate `"ltr"`.
 */
const NOT_COPY: RegExp[] = [
  /^v3\.header\.otherLocaleLang$/,   // "ar" / "en" — goes in a lang attribute
  /^v3\.cms\./,                      // the admin console is not a public screen
  /^app\.admin\./,                   // same
  /^app\.meta\.dir$/,                // "rtl" / "ltr"
  /^app\.icons\./,                   // the icon-sheet page, a build tool
];

export const isEditable = (key: string) => !NOT_COPY.some((re) => re.test(key));

/* ── the length budget ───────────────────────────────────────────────────── */

/**
 * How long a replacement may be, derived from the DESIGNED wording.
 *
 * "Derived so the layout never breaks" means the budget has to come from the
 * slot, and the only honest measure of the slot we have is what the designer
 * put in it. So: the shorter the original, the tighter the box it sits in.
 *
 *   ≤ 12 chars   a button or a chip — eight characters of headroom, no more
 *   ≤ 40 chars   a heading or a label — half again
 *   longer       a paragraph — a third again
 *
 * Taken across BOTH locales, so an English word does not get a tighter budget
 * than the Arabic that shares its slot.
 */
export function maxLengthFor(...defaults: (string | undefined)[]): number {
  const len = Math.max(0, ...defaults.map((d) => (d ?? "").length));
  if (len <= 12) return len + 8;
  if (len <= 40) return Math.ceil(len * 1.5);
  return Math.ceil(len * 1.3);
}

/* ── folding overrides back into a dictionary ────────────────────────────── */

/** Set `a.b.c` on a nested object, in place. Unknown paths are ignored. */
function setPath(target: Record<string, unknown>, path: string, value: string): void {
  const parts = path.split(".");
  let node: Record<string, unknown> = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const next = node[parts[i]];
    /* A key that does not exist in the dictionary is a key from an older or
     * newer release. Ignored rather than created: an override has to override
     * something, and inventing `s.hero.subtitel` would render nowhere and be
     * impossible to find. */
    if (!next || typeof next !== "object") return;
    node = next as Record<string, unknown>;
  }
  const last = parts[parts.length - 1];
  if (typeof node[last] !== "string") return;
  node[last] = value;
}

/**
 * A dictionary with this market and locale's overrides applied.
 *
 * Returns the ORIGINAL object when there is nothing to apply, so the common
 * case allocates nothing and `===` still holds for memo comparisons.
 */
export function withOverrides<T>(dictionary: T, ns: Namespace, texts: TextMap | undefined): T {
  if (!texts) return dictionary;
  const prefix = `${ns}.`;
  const mine = Object.entries(texts).filter(([k]) => k.startsWith(prefix));
  if (!mine.length) return dictionary;
  const copy = structuredClone(dictionary) as Record<string, unknown>;
  for (const [key, value] of mine) {
    if (typeof value !== "string" || !value) continue;
    setPath(copy, key.slice(prefix.length), value);
  }
  return copy as T;
}

/* ── where a key belongs in the editor ───────────────────────────────────── */

export type Group = { page: string; section: string };

/* Longest prefix wins, so `v3.detail.advisorTitle` lands on the listing page
 * rather than in the advisor band. Titles are Arabic because the console is. */
const GROUPS: [RegExp, Group][] = [
  [/^v3\.header\./, { page: "مشترك", section: "الهيدر" }],
  [/^v3\.nav\./, { page: "مشترك", section: "الهيدر — روابط التنقل" }],
  [/^v3\.marketSwitcher\./, { page: "مشترك", section: "الهيدر — مبدّل السوق" }],
  [/^v3\.marketSuggestion\./, { page: "مشترك", section: "شريط اقتراح السوق" }],
  [/^v3\.footer\./, { page: "مشترك", section: "الفوتر" }],
  [/^v3\.listing\./, { page: "مشترك", section: "بطاقة الإعلان" }],
  [/^v3\.types\./, { page: "مشترك", section: "أنواع العقارات" }],

  [/^v3\.hero\./, { page: "الرئيسية", section: "قسم البداية والبحث" }],
  [/^v3\.sections\./, { page: "الرئيسية", section: "عناوين الأقسام" }],
  [/^v3\.featuredStates\./, { page: "الرئيسية", section: "العقارات المميزة — الحالات" }],
  [/^v3\.advisor\./, { page: "الرئيسية", section: "المستشار الذكي" }],
  [/^v3\.listerBand\./, { page: "الرئيسية", section: "شريط المعلنين" }],
  [/^v3\.plan\./, { page: "الرئيسية", section: "بطاقات الباقات" }],

  [/^v3\.results\./, { page: "نتائج البحث", section: "الفلاتر والنتائج" }],
  [/^v3\.detail\./, { page: "صفحة الإعلان", section: "التفاصيل" }],
  [/^v3\.post\./, { page: "اعرض عقارك", section: "خطوات النشر" }],
  [/^v3\.plans\./, { page: "الباقات", section: "الباقات" }],
  [/^v3\.account\./, { page: "لوحة التحكم", section: "لوحة التحكم" }],
  [/^v3\.chat\./, { page: "الرسائل", section: "الرسائل" }],
  [/^v3\.profile\./, { page: "الحساب والملف العام", section: "الحساب" }],

  [/^app\.auth\./, { page: "تسجيل الدخول", section: "النموذج" }],
  [/^app\.register\./, { page: "إنشاء حساب", section: "النموذج" }],
  [/^app\.panel\./, { page: "اللوحة السابقة", section: "اللوحة" }],
  [/^app\.chat\./, { page: "اللوحة السابقة", section: "المستشار" }],
  [/^app\.pricing\./, { page: "الباقات", section: "الدفع والاشتراك" }],
  [/^app\.notFound\./, { page: "مشترك", section: "صفحة غير موجودة" }],
  [/^app\.privacy\./, { page: "الخصوصية", section: "الصفحة" }],
  [/^app\.status\./, { page: "مشترك", section: "إشعارات الحالة" }],
  [/^app\.meta\./, { page: "مشترك", section: "عناوين المتصفح ومحركات البحث" }],
  [/^app\.agent\./, { page: "لوحة الوسيط", section: "اللوحة" }],
];

export function groupOf(key: string): Group {
  for (const [re, g] of GROUPS) if (re.test(key)) return g;
  /* Anything unclassified still shows, under its own top-level name, rather
   * than disappearing from the editor because nobody updated this table. */
  const top = key.split(".").slice(0, 2).join(".");
  return { page: "غير مصنّف", section: top };
}
